// NotificationContainer.tsx

import React, { useEffect, useState } from "react"
import { NOTIFICATION_TIMER } from "../../../../utils/constants"

type NotificationContainerProps = {
  errorMessage: string
  successMessage: string
}
type NotificationMessage = {
  successMessage: string
  errorMessage: string
  severity: string
  id: number
  message: string
  type: "success" | "error"
}

const NotificationContainer: React.FC<NotificationContainerProps> = ({
  errorMessage,
  successMessage,
}) => {
  const [notifications, setNotifications] = useState<NotificationMessage[]>([])

  useEffect(() => {
    if (errorMessage) {
      const newErrorNotification: NotificationMessage = {
        id: new Date().getTime(),
        errorMessage,
        severity: "error",
        successMessage: "",
        message: "",
        type: "error",
      }
      setNotifications((prevNotifications) => [
        ...prevNotifications,
        newErrorNotification,
      ])

      const timeoutId = setTimeout(() => {
        setNotifications((prevNotifications) =>
          prevNotifications.filter(
            (notification) => notification.id !== newErrorNotification.id,
          ),
        )
      }, NOTIFICATION_TIMER)

      return () => clearTimeout(timeoutId) // Cleanup function
    }
  }, [errorMessage])

  useEffect(() => {
    if (successMessage) {
      const newSuccessNotification: NotificationMessage = {
        id: new Date().getTime(),
        successMessage,
        severity: "success",
        errorMessage: "",
        message: "",
        type: "error",
      }
      setNotifications((prevNotifications) => [
        ...prevNotifications,
        newSuccessNotification,
      ])

      const timeoutId = setTimeout(() => {
        setNotifications((prevNotifications) =>
          prevNotifications.filter(
            (notification) => notification.id !== newSuccessNotification.id,
          ),
        )
      }, 6000)

      return () => clearTimeout(timeoutId) // Cleanup function
    }
  }, [successMessage])

  useEffect(() => {
    // Reset error message when component unmounts
    return () => {
      if (errorMessage) {
        setNotifications((prevNotifications) =>
          prevNotifications.filter(
            (notification) => notification.severity !== "error",
          ),
        )
      }
    }
  }, [])

  const handleCloseNotification = (id: number) => {
    setNotifications((prevNotifications) =>
      prevNotifications.filter((notification) => notification.id !== id),
    )
  }

  return (
    <>
      <div
        className={`fixed top-4 right-4 flex flex-col items-start opacity-100`}>
        {notifications.map((notification, index) => (
          <div
            key={notification.id}
            className={`w-64 top-${
              4 + index * 1
            } flex justify-between items-center p-4 mb-4 text-sm rounded-lg ${
              notification.severity === "success"
                ? "text-green-800 bg-green-50"
                : "text-red-800 bg-red-50"
            } dark:bg-gray-800`}
            role="alert">
            <span className="font-medium alerts">
              {notification.severity === "success"
                ? notification.successMessage
                : notification.errorMessage}
            </span>
            <button
              type="button"
              className={`rounded-3xl focus:ring-4 p-1.5 inline-flex items-center justify-center h-8 w-8 ${
                notification.severity === "success"
                  ? "bg-green-50 text-green-500 focus:ring-green-400 hover:bg-green-200 dark:text-green-400 dark:hover:bg-gray-700"
                  : "bg-red-50 text-red-500 focus:ring-red-400 hover:bg-red-200 dark:text-red-400 dark:hover:bg-gray-700"
              }`}
              onClick={() => handleCloseNotification(notification.id)}
              aria-label="Close">
              <span className="sr-only">Close</span>
              <svg
                className="w-3 h-3"
                aria-hidden="true"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 14 14">
                <path
                  stroke={notification.severity === "success" ? "green" : "red"}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="m1 1 6 6m0 0 6 6M7 7l6-6M7 7l-6 6"
                />
              </svg>
            </button>
          </div>
        ))}
      </div>
    </>
  )
}

export default NotificationContainer
